import { queryClient } from 'lib/queryClient'

import { accessControlApi } from '../pages/AccessControl/api/access-control.client'

import { can } from './ability'
import { RbacActions, RbacSubjects } from './constants'
import { isRbacEnforcementEnabled } from './enforcement-flag'
import { MY_ABILITY_QUERY_KEY } from './useMyAbilityQuery'
import { navigationProgress } from './navigation-progress'

interface OnEnterError {
  status: number
  message: string
}

interface RouterLocation {
  pathname: string
}

interface NextState {
  location: RouterLocation
}

type Replace = (pathname: string) => void
type Callback = (error?: OnEnterError) => void

/**
 * Pathnames always reachable regardless of the AdminPortal grant:
 *  - Home ('/') so a just-granted user can re-navigate from it (and so we never
 *    redirect Home onto itself).
 *  - '/profile' — available to any admin role; it is not an admin-portal
 *    surface, so it keeps its own coarse role gate without the AdminPortal floor.
 */
const ALWAYS_ALLOWED_PATHNAMES = new Set(['/', '', '/profile'])

/**
 * Single admin-shell route gate. Applied ONCE on the parent `/` (AdminLayout)
 * route, it runs for every admin child transition (react-router-3 runs each
 * matched route's onEnter parent-to-child), so individual admin routes no
 * longer need their own Access/AdminPortal gate.
 *
 * Behaviour:
 *  - The coarse role check is expected to have ALREADY passed (the caller wraps
 *    this after authOnEnter). This helper only adds the DB-ability floor.
 *  - The Home/index route ('/') is always allowed — a blocked user lands there,
 *    where HomeView shows a "no admin portal access" message + Refresh (it does
 *    its own Access/AdminPortal check), and can retry after being granted.
 *  - For any deeper admin route, require can(Access, AdminPortal). On a miss (or
 *    a resolution error — fail-closed), REPLACE to Home rather than calling back
 *    with an error (which would 404). Finer per-route subjects
 *    (FundingInvoice/Settings/PermissionManagement) are still enforced by those
 *    routes' own abilityOnEnter on top of this floor.
 *
 * UX only — the EPS AbilityGuard is the authoritative boundary.
 */
export async function adminShellOnEnter(
  nextState: NextState,
  replace: Replace,
  callback: Callback
): Promise<void> {
  const pathname = nextState?.location?.pathname ?? '/'
  if (ALWAYS_ALLOWED_PATHNAMES.has(pathname)) {
    callback()
    return
  }
  // Kill-switch: when RBAC enforcement is OFF (or LD unreachable — fail-safe),
  // this admin-shell floor is a no-op and admin routes keep only their coarse
  // role check, matching the EPS server. Checked before any fetch.
  if (!(await isRbacEnforcementEnabled())) {
    callback()
    return
  }
  // The fetch below blocks the route transition; show the nav progress bar so
  // the click visibly responds instead of feeling dead.
  navigationProgress.start()
  try {
    // staleTime: 0 — always re-check against the server on route entry so a
    // permission change reflects on the very next navigation (no 5-min lag, no
    // manual refresh). This also primes the shared cache the menu reads.
    const data = await queryClient.fetchQuery({
      queryKey: MY_ABILITY_QUERY_KEY,
      queryFn: () => {
        return accessControlApi.getMyAbility().catch((error) => {
          const status = (error as { response?: { status?: number } })?.response
            ?.status
          if (status === 403) {
            localStorage.removeItem('admin-token')
            if (window.location.pathname !== '/login') {
              window.location.assign('/login')
            }
          }
          throw error
        })
      },
      staleTime: 0,
    })
    if (can(data?.rules, RbacActions.Access, RbacSubjects.AdminPortal)) {
      callback()
      return
    }
    replace('/')
    callback()
  } catch {
    replace('/')
    callback()
  } finally {
    navigationProgress.stop()
  }
}
